/**
 * Run a DOM update as a View Transition when the browser supports it and
 * the user has not asked for reduced motion. Elements with a
 * `view-transition-name` then move smoothly to their new place; everything
 * else cross-fades. Without support the update just happens.
 *
 * Used for page changes (router), filter changes and removals in grids.
 */
interface ViewTransition {
  finished: Promise<void>;
  ready: Promise<void>;
  updateCallbackDone: Promise<void>;
}

interface ViewTransitionDocument {
  startViewTransition?: (update: () => void) => ViewTransition;
}

let running = false;

export function withTransition(update: () => void): void {
  const doc = document as unknown as ViewTransitionDocument;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Nested transitions would abort each other; run inner ones plainly.
  if (doc.startViewTransition === undefined || reduce || running) {
    update();
    return;
  }
  running = true;
  const transition = doc.startViewTransition.call(document, update);
  // A transition can be skipped by the browser (tab hidden, layout changed
  // mid-flight). The update still runs; only the animation is dropped.
  // Swallow those rejections so they do not surface as errors.
  const ignore = (): void => undefined;
  transition.ready.catch(ignore);
  transition.updateCallbackDone.catch(ignore);
  transition.finished.then(
    () => {
      running = false;
    },
    () => {
      running = false;
    },
  );
}
